import {
  createTransportClient,
  getTransportTokenForOrganization,
  TransportRequestError,
} from "@/transport"

import type { OverageSettingsPort } from "../hooks/useOverageSettingsEditor"

interface ConnectionOptions {
  clerkOrganizationId: string
  organizationSlug: string
  getToken: (options?: {
    organizationId?: string
    skipCache?: boolean
  }) => Promise<string | null>
  invalidate: () => Promise<void>
  signal: AbortSignal
}

function classifyFailure(error: unknown) {
  if (error instanceof TransportRequestError) {
    if (error.detail?.code === "settings_conflict" && error.status === 409) {
      return "conflict"
    }
    if (error.detail?.code === "not_configured" && error.status === 409) {
      return "not-configured"
    }
    if (
      error.status >= 400 &&
      error.status < 500 &&
      error.detail?.code &&
      [
        "invalid_request",
        "permission_denied",
        "authentication_required",
      ].includes(error.detail.code)
    ) {
      return "rejected"
    }
  }
  return "uncertain"
}

export async function connectOverageSettings({
  clerkOrganizationId,
  organizationSlug,
  getToken,
  invalidate,
  signal,
}: ConnectionOptions): Promise<{
  organizationId: string
  port: OverageSettingsPort
}> {
  if (!clerkOrganizationId || !organizationSlug) {
    throw new Error("An explicit organization membership is required.")
  }
  const client = createTransportClient({
    getToken: () =>
      getTransportTokenForOrganization({
        getToken,
        organizationId: clerkOrganizationId,
        organizationSlug,
        requireOrganizationClaims: true,
      }),
    getOrganizationContext: () => ({ slug: organizationSlug }),
  })
  const { organization } = await client.operations.getOrganizationRecord(
    { skip_cache: true },
    { signal },
  )
  signal.throwIfAborted()
  if (
    organization?.clerk_id !== clerkOrganizationId ||
    typeof organization.id !== "string" ||
    organization.id.length === 0 ||
    organization.id.length > 512
  ) {
    throw new Error("The organization identity could not be verified.")
  }
  const organizationId = organization.id
  const headers = { "X-Coval-Organization-Id": organizationId }
  return {
    organizationId,
    port: {
      read: (readSignal) =>
        client.operations.getBillingOverageSettings({
          headers,
          signal: readSignal,
        }),
      update: (request, updateSignal) =>
        client.operations.updateBillingOverageSettings(request, {
          headers,
          signal: updateSignal,
        }),
      invalidate,
      classifyFailure,
    },
  }
}
